import { Link } from "react-router-dom";
import PageCTA from "@/components/PageCTA";
import { ArrowRight, Plane } from "lucide-react";
import { Reveal, HighlandBackdrop, CinematicTitle } from "@/components/Reveal";
import { useContent } from "@/lib/useContent";

const PORTRAIT = "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const SECONDARY = "https://images.pexels.com/photos/33677969/pexels-photo-33677969.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
    const { data: hero } = useContent("about_hero", {
        overline: "About",
        title: "A patient camera, in difficult weather.",
        lede: "",
    });
    const { data: story } = useContent("about_story", {
        pull_quote: "",
        paragraphs: [],
    });
    const { data: kit } = useContent("about_kit", {
        overline: "The kit",
        title: "What I fly",
        items: [],
    });

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="about-page">
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
                <div className="lg:col-span-7">
                    <Reveal>
                        <div className="overline mb-5"><span className="editorial-rule" />{hero.overline}</div>
                    </Reveal>
                    <CinematicTitle
                        text={hero.title}
                        testid="about-title"
                        className="mb-10"
                        size="clamp(2.8rem, 7vw, 7rem)"
                    />
                    <Reveal delay={0.5}>
                        <p className="text-xl text-[var(--sb-text-dim)] leading-relaxed max-w-xl">
                            {hero.lede}
                        </p>
                    </Reveal>
                </div>
                <Reveal delay={0.3} className="lg:col-span-5 lg:col-start-8">
                    <div className="relative aspect-[3/4] overflow-hidden border border-[var(--sb-border)]">
                        <img src={PORTRAIT} alt="Field portrait" className="w-full h-full object-cover slow-zoom-ne" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/40 via-transparent to-transparent" />
                    </div>
                </Reveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
                <div className="lg:col-span-8 lg:col-start-3 text-[var(--sb-text-dim)] space-y-7 text-lg leading-[1.85]">
                    {story.pull_quote && (
                        <Reveal>
                            <p className="font-serif italic text-[var(--sb-text)] leading-[1.15]" style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)" }}>
                                &ldquo;{story.pull_quote}&rdquo;
                            </p>
                        </Reveal>
                    )}
                    {(story.paragraphs || []).map((p, i) => (
                        <Reveal key={i} delay={0.08 + i * 0.05}><p>{p}</p></Reveal>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <Reveal className="lg:col-span-6">
                    <div className="relative aspect-[5/4] overflow-hidden border border-[var(--sb-border)]">
                        <img src={SECONDARY} alt="Highland road" className="w-full h-full object-cover slow-zoom-sw" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/40 via-transparent to-transparent" />
                    </div>
                </Reveal>
                <div className="lg:col-span-5 lg:col-start-8">
                    <Reveal>
                        <div className="overline mb-3"><span className="editorial-rule" />{kit.overline}</div>
                        <h3 className="font-serif text-4xl tracking-tight mb-8">{kit.title}</h3>
                    </Reveal>

                    {/* Pilot's badge — a proper flight-log-styled chip with
                        the aircraft model + take-off weight, framed like an
                        official identification card. */}
                    <Reveal delay={0.12}>
                        <div
                            className="relative inline-flex items-center gap-4 border border-[var(--sb-brand)]/60 bg-[var(--sb-bg)]/60 backdrop-blur-md px-5 py-4 mb-8 group"
                            style={{ borderRadius: 4 }}
                            data-testid="pilot-badge"
                        >
                            {/* Blue notch to imply a punched aircraft ID card */}
                            <span className="absolute -left-px top-1/2 -translate-y-1/2 w-1.5 h-8 bg-[var(--sb-brand)]" />
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-[var(--sb-brand)]/50 text-[var(--sb-brand)] shrink-0">
                                <Plane size={18} className="rotate-45" />
                            </div>
                            <div className="flex flex-col leading-tight">
                                <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--sb-brand)] mb-1">Pilot&apos;s Badge</span>
                                <span className="font-serif text-lg text-[var(--sb-text)]">DJI Air 3</span>
                                <span className="text-xs tracking-[0.16em] uppercase text-[var(--sb-text-dim)] mt-0.5">MTOW · 720 g</span>
                            </div>
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--sb-brand)] animate-pulse ml-2" />
                        </div>
                    </Reveal>

                    <ul className="space-y-3 text-[var(--sb-text-dim)] text-lg">
                        {(kit.items || []).map((item, i) => (
                            <Reveal key={i} delay={0.2 + i * 0.05}>
                                <li className="flex items-start gap-3 border-b border-[var(--sb-border)] pb-3">
                                    <span className="text-[var(--sb-brand)] mt-1">·</span>
                                    <span>{item}</span>
                                </li>
                            </Reveal>
                        ))}
                    </ul>
                    <Reveal delay={0.4}>
                        <div className="mt-10">
                            <Link to="/contact" className="btn-primary" data-testid="about-contact-cta">
                                Get in touch <ArrowRight size={18} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
            <PageCTA variant="flight" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
